/**
 * B2B Team Try-at-home Box — cart gift engine.
 * --------------------------------------------
 * Adds the free Team Try-at-home Box (SKU SYB-B2B-TRYBOX) to a qualifying B2B
 * cart, and removes it again if the cart drops back below the bar.
 *
 * WHY IT IS IN THE CART AND NOT A TAG OR AN ORDER EDIT
 * Picqer snapshots Shopify's tag list into its `Order tags Shopify` orderfield
 * ONCE, at import, and imports a storefront B2B order in ~6 seconds — before
 * Shopify has applied any tags. Measured 2026-08-23: 11 of 22 B2B orders have no
 * such orderfield at all, which is why the previous tag-then-Picqer-rule design
 * granted 0 boxes in a month. Adding the line AFTER the order exists is worse: a
 * Shopify line the Picqer twin lacks is a `shopify_has_more` mismatch and
 * reconcile_orders PAUSES the picklist for it, so a free gift would hold a real
 * paid B2B order off the warehouse floor.
 *
 * The line therefore has to exist before the order does. Once it is on the order
 * at creation, Picqer imports it like any other line, matches SKU
 * SYB-B2B-TRYBOX to its virtual_composition and explodes it into the 3 mixes,
 * 4 seed packets and the A6 card. No Picqer change, no rule, no race.
 *
 * DELIBERATELY A SIBLING OF syba-cart-gift-engine.js, NOT AN EXTENSION OF IT
 * That engine's first hard rule is "skip entirely in B2B contexts", and it is
 * wired into Kaching's drawer, which b2b-context.liquid hides for B2B customers.
 * Bolting B2B logic into it would put a live D2C gift path at risk to save a
 * file. This one runs ONLY for B2B and touches nothing else.
 *
 * ELIGIBILITY IS NOT DECIDED HERE. snippets/nf-trybox-config.liquid renders
 * `enabled` server-side from the company's store type and the trybox_granted
 * latch. This file only watches the money.
 *
 * HARD RULES
 *   - Never throw. A broken cart is worse than a missed gift.
 *   - Never touch a cart when cfg.enabled is false — not even a read.
 *   - The box's own price never counts toward the bar (it is 0, but a future
 *     priced gift must not bootstrap itself over the line either).
 */
(function () {
  'use strict';

  var cfg = window.SybaTryboxConfig;
  if (!cfg || cfg.enabled !== true || !cfg.variant_id) return;

  // Half the B2B catalogues price EX VAT (EU reverse charge), half IN VAT
  // (domestic). The cart JSON carries no tax breakdown, so gross the bar up
  // rather than trying to net the cart down. See the OPEN QUESTION in
  // nf-trybox-config.liquid: vat_bps is a single-rate assumption and the error
  // direction is conservative (withholds a gift rather than giving one away).
  var THRESHOLD = cfg.taxes_included
    ? Math.round(cfg.threshold_cents_ex_vat * (10000 + (cfg.vat_bps || 0)) / 10000)
    : cfg.threshold_cents_ex_vat;

  var VARIANT = cfg.variant_id;
  var MARKER = cfg.marker || 'trybox';
  var busy = false;
  // Last evaluated eligibility key: "<non-box cents>:s|n" (s = the cart still
  // holds a shippable non-box line). Adding or removing a EUR 0 box never moves
  // it, so this both debounces and prevents an add/remove loop; the :s/:n half
  // keeps a same-price swap (physical line → gift card) from being skipped.
  // -1 = never evaluated; it can never equal a key, so a reset re-opens it.
  var lastTotal = -1;

  function post(url, body) {
    busy = true;
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body)
    }).then(function () {
      busy = false;
    }).catch(function () {
      busy = false;
      lastTotal = -1; // allow a retry on the next cart change
    });
  }

  function evaluate() {
    if (busy) return;
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart || !cart.items) return;
        var box = null;
        var goodsCents = 0;
        var shippableGoods = false;
        cart.items.forEach(function (item) {
          if (item.variant_id === VARIANT) { box = item; return; }
          goodsCents += item.final_line_price;
          // Gift cards (and any other no-ship product) report
          // requires_shipping === false. Anything else counts as shippable —
          // fail-closed, a missing flag keeps the previous behaviour.
          if (item.requires_shipping !== false) { shippableGoods = true; }
        });
        // Same defect class as the D2C engine's, fixed there 2026-09-13: a cart
        // of ONLY no-ship lines (digital gift cards) needs no delivery until we
        // push a PHYSICAL free box into it. That flips cart.requires_shipping,
        // and Shopify's price-based rate conditions only count items that
        // require shipping — so the gift-card money never reaches the free
        // band and checkout charges the paid rate for a €0 box.
        var shippableKey = goodsCents + (shippableGoods ? ':s' : ':n');
        if (shippableKey === lastTotal) return;
        lastTotal = shippableKey;

        // strictly ABOVE, per the rule — and only for a cart that ships
        var eligible = shippableGoods && goodsCents > THRESHOLD;
        if (eligible && !box) {
          post('/cart/add.js', {
            items: [{ id: VARIANT, quantity: 1, properties: { _free_gift: MARKER } }]
          });
        } else if (!eligible && box) {
          post('/cart/change.js', { id: box.key, quantity: 0 });
        } else if (eligible && box && box.quantity !== 1) {
          // Someone bumped the quantity. One box per first order, always.
          post('/cart/change.js', { id: box.key, quantity: 1 });
        }
      })
      .catch(function () { /* never throw out of the cart path */ });
  }

  var timer = null;
  function schedule() {
    if (timer) clearTimeout(timer);
    // Long enough for the theme's own cart request to settle, short enough that
    // the box is on screen before the buyer reaches checkout.
    timer = setTimeout(function () { timer = null; evaluate(); }, 350);
  }

  /* Observe cart mutations without depending on which cart UI the B2B ordering
     page uses. The theme publishes PUB_SUB_EVENTS.cartUpdate, but the B2B page
     is not guaranteed to route through it, so the fetch wrapper is the reliable
     signal and the event is a bonus.

     WHY THIS RE-ASSERTS ITSELF. Wrapping window.fetch once is not enough: another
     script that wraps it AFTER us captures the then-current fetch and calls that,
     which orphans our wrapper completely. Measured live on the B2B ordering page
     2026-08-24 -- window.fetch was a 2.4 KB third-party wrapper carrying none of
     our markers, so the engine never saw a single cart write and no box was ever
     added. Being loaded first is not something a theme can guarantee.

     So we re-wrap whenever we notice we are no longer on top. The interval is
     cheap (a reference comparison, no network) and bounded: a third party that
     also re-wraps on a timer would otherwise build an unbounded chain, so we stop
     after MAX_REWRAPS and let the pub/sub path carry it.

     Deliberately NOT a /cart.js poll. syba-cart-gift-engine.js documents a
     /cart.js 429 storm as the thing its fetch-gate exists to prevent, and a timer
     hitting the same endpoint would walk straight back into it. */
  var CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  var MAX_REWRAPS = 10;
  var rewraps = 0;
  var ourFetch = null;

  function installHook() {
    try {
      if (typeof window.fetch !== 'function' || window.fetch === ourFetch) return;
      var previous = window.fetch;
      ourFetch = function (input) {
        var url = '';
        try {
          url = typeof input === 'string' ? input : (input && input.url) || '';
        } catch (e) { /* exotic Request, fall through */ }
        var result = previous.apply(this, arguments);
        // Our own writes must not re-trigger us; `busy` covers that window.
        if (url && CART_WRITE.test(url) && !busy) {
          try { result.then(schedule, function () {}); } catch (e) {}
        }
        return result;
      };
      window.fetch = ourFetch;
      rewraps += 1;
    } catch (e) { /* leave fetch alone if anything is odd */ }
  }

  installHook();
  var hookGuard = setInterval(function () {
    if (rewraps >= MAX_REWRAPS) { clearInterval(hookGuard); return; }
    if (window.fetch !== ourFetch) {
      installHook();
      // Someone wrapped us mid-session, so the cart may have moved while we were
      // deaf. Re-evaluate rather than wait for the next write.
      schedule();
    }
  }, 1000);

  try {
    if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS &&
        window.PUB_SUB_EVENTS.cartUpdate) {
      subscribe(window.PUB_SUB_EVENTS.cartUpdate, schedule);
    }
  } catch (e) { /* pub/sub is optional */ }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', evaluate);
  } else {
    evaluate();
  }
})();
